import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

import AuthLoadingContainer from "../AuthLoadingContainer";
import { ConfigContext } from "../../Config/ConfigContext";
import useApi from "../../hooks/useApi";

/** Google Identity Services client script URL. */
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";
/** Default visual configuration applied to the rendered GIS button. */
const DEFAULT_BUTTON_OPTIONS = {
	type: "standard",
	theme: "outline",
	size: "large"
};
/** Default resilience flags forwarded to google.accounts.id.initialize. */
const DEFAULT_INITIALIZE_OPTIONS = {
	itp_support: true,
	use_fedcm_for_prompt: true,
	auto_select: false
};

/**
 * GoogleSignIn consolidates Google Identity Services script injection, button rendering,
 * UX fallback handling, and the server-side auth init call into a single component.
 *
 * @param {Object} props - Component props.
 * @param {React.RefObject<HTMLElement>} [props.parentBtnRef] - External ref where the GIS button should render.
 * @param {function} props.setAuthSuccess - Callback invoked after api.auth.init succeeds.
 * @param {function} [props.handleAuthError] - Optional error handler exposed to the parent view.
 * @param {string} [props.signInBackgroundColor="#23102E"] - Background color used behind the GIS button.
 * @param {string} [props.nonce] - Optional nonce applied to the injected script tag.
 * @param {function} [props.onScriptLoadSuccess] - Callback fired when the GIS script loads.
 * @param {function} [props.onScriptLoadError] - Callback fired if the GIS script fails to load.
 * @param {Object} [props.buttonOptions] - Visual configuration forwarded to renderButton.
 * @param {Object} [props.initializeOptions] - Additional flags forwarded to google.accounts.id.initialize.
 * @param {("popup"|"redirect")} [props.initialUxMode="popup"] - Preferred UX mode (falls back to redirect on errors).
 * @param {string} [props.clientId] - Optional client ID override (falls back to ConfigContext when omitted).
 * @param {Object} [props.containerProps] - Extra props passed to the Box wrapper.
 * @returns {JSX.Element} Fully-wired Google sign-in experience.
 */
export default function GoogleSignIn({
	parentBtnRef,
	setAuthSuccess,
	handleAuthError,
	nonce,
	onScriptLoadSuccess,
	onScriptLoadError,
	buttonOptions = {},
	initializeOptions = {},
	initialUxMode = "popup",
	clientId: clientIdOverride,
	containerProps = {}
}) {
	const api = useApi();
	const { config } = useContext(ConfigContext);
	const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = useState(false);
	const [uxMode, setUxMode] = useState(initialUxMode);
	const internalButtonRef = useRef(null);
	const onScriptLoadSuccessRef = useRef(onScriptLoadSuccess);
	onScriptLoadSuccessRef.current = onScriptLoadSuccess;
	const onScriptLoadErrorRef = useRef(onScriptLoadError);
	onScriptLoadErrorRef.current = onScriptLoadError;

	const resolvedClientId = clientIdOverride ?? config?.googleOAuth?.clientId;
	const { sx: containerSx, ...restContainerProps } = containerProps;

	const memoizedButtonOptions = useMemo(() => ({
		...DEFAULT_BUTTON_OPTIONS,
		...buttonOptions
	}), [buttonOptions]);

	const memoizedInitializeOptions = useMemo(() => ({
		...DEFAULT_INITIALIZE_OPTIONS,
		...initializeOptions
	}), [initializeOptions]);

	useEffect(() => {
		setUxMode(initialUxMode);
	}, [initialUxMode]);

	useEffect(() => {
		if (!resolvedClientId) {
			// eslint-disable-next-line no-console
			console.error("GoogleSignIn requires a clientId via props or ConfigContext.");
		}
	}, [resolvedClientId]);

	const handleRenderError = useCallback((error) => {
		if (error && typeof handleAuthError === "function") {
			handleAuthError(error);
		}
		setUxMode((currentMode) => (currentMode === "redirect" ? currentMode : "redirect"));
	}, [handleAuthError]);

	const handleCredentialResponse = useCallback(async (credentialResponse) => {
		if (!credentialResponse?.credential) {
			handleRenderError();
			return;
		}

		const normalizedResponse = {
			credential: credentialResponse.credential,
			clientId: credentialResponse.clientId ?? credentialResponse.client_id,
			select_by: credentialResponse.select_by
		};

		try {
			const params = {
				oauthResponse: JSON.stringify(normalizedResponse),
				signInPlatform: "google",
				signInToken: normalizedResponse.credential
			};

			await api.auth.init({ params });
			setAuthSuccess(true);
		} catch (error) {
			if (typeof handleAuthError === "function") {
				handleAuthError(error);
			}
			// eslint-disable-next-line no-console
			console.error("Auth initialization failed:", error);
		}
	}, [api, handleAuthError, handleRenderError, setAuthSuccess]);

	useEffect(() => {
		const script = document.createElement("script");
		script.async = true;
		script.defer = true;
		script.src = SCRIPT_SRC;
		if (nonce) {
			script.nonce = nonce;
		}

		script.onload = () => {
			setScriptLoadedSuccessfully(true);
			onScriptLoadSuccessRef.current?.();
		};

		script.onerror = () => {
			setScriptLoadedSuccessfully(false);
			onScriptLoadErrorRef.current?.();
		};

		document.body.appendChild(script);
		return () => {
			if (script.parentNode) {
				script.parentNode.removeChild(script);
			}
		};
	}, [nonce]);

	useEffect(() => {
		if (!scriptLoadedSuccessfully || !resolvedClientId) {
			return;
		}

		const targetNode = parentBtnRef?.current ?? internalButtonRef.current;
		const googleAccounts = window?.google?.accounts?.id;
		if (!targetNode || !googleAccounts) {
			return;
		}

		targetNode.innerHTML = "";

		googleAccounts.initialize({
			...memoizedInitializeOptions,
			ux_mode: uxMode,
			client_id: resolvedClientId,
			callback: handleCredentialResponse,
			error_callback: handleRenderError
		});

		googleAccounts.renderButton(targetNode, memoizedButtonOptions);
	}, [
		scriptLoadedSuccessfully,
		resolvedClientId,
		parentBtnRef,
		memoizedInitializeOptions,
		memoizedButtonOptions,
		uxMode,
		handleCredentialResponse,
		handleRenderError
	]);

	const mergedContainerSx = {
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		...(containerSx || {})
	};

	if (!resolvedClientId) {
		return (
			<Box sx={mergedContainerSx} {...restContainerProps}>
				Missing Google OAuth client ID configuration.
			</Box>
		);
	}

	return (
		<>
			{!scriptLoadedSuccessfully && <AuthLoadingContainer />}
			{scriptLoadedSuccessfully && (
				<Box sx={mergedContainerSx} {...restContainerProps}>
					{!parentBtnRef && (
						<div
							ref={internalButtonRef}
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%"
							}}
						/>
					)}
				</Box>
			)}
		</>
	);
}

GoogleSignIn.propTypes = {
	parentBtnRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any })
	]),
	setAuthSuccess: PropTypes.func.isRequired,
	handleAuthError: PropTypes.func,
	signInBackgroundColor: PropTypes.string,
	nonce: PropTypes.string,
	onScriptLoadSuccess: PropTypes.func,
	onScriptLoadError: PropTypes.func,
	buttonOptions: PropTypes.shape({
		type: PropTypes.string,
		theme: PropTypes.string,
		size: PropTypes.string,
		text: PropTypes.string,
		shape: PropTypes.string,
		logo_alignment: PropTypes.string,
		width: PropTypes.number,
		locale: PropTypes.string
	}),
	initializeOptions: PropTypes.object,
	initialUxMode: PropTypes.oneOf(["popup", "redirect"]),
	clientId: PropTypes.string,
	containerProps: PropTypes.object
};